import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useProfile } from "@/hooks/useProfile";
import type { PetRelationshipKind } from "@/pages/daycare-hotel/reservation/petProfileModel";
import { EMPTY_PET_DAYCARE_HOTEL_DRAFT, type PetDaycareHotelDraft } from "./petDaycareHotelDraft";

export const petDaycareHotelDraftKey = (petId: string) => ["pet-daycare-hotel-draft", petId];

export function usePetDaycareHotelInitialDraft(petId: string | null | undefined) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: petDaycareHotelDraftKey(petId ?? ""),
    enabled: !!petId && !!clinicId,
    queryFn: async (): Promise<PetDaycareHotelDraft> => {
      const [notesResult, packsResult, alertsResult, itemsResult, relationshipsResult, activitiesResult] = await Promise.all([
        supabase.from("pets").select("daycare_hotel_notes").eq("id", petId!).maybeSingle(),
        supabase
          .from("stay_packs")
          .select("id, stay_pack_members!inner(pet_id)")
          .eq("clinic_id", clinicId!)
          .eq("is_active", true)
          .eq("stay_pack_members.pet_id", petId!),
        supabase
          .from("pet_custom_alerts")
          .select("id, label, value, color")
          .eq("pet_id", petId!)
          .eq("is_active", true),
        supabase
          .from("pet_stored_items")
          .select("id, item_name, storage_location")
          .eq("pet_id", petId!)
          .eq("is_kept", true),
        supabase
          .from("pet_relationships")
          .select("id, pet_id, related_pet_id, relationship")
          .or(`pet_id.eq.${petId},related_pet_id.eq.${petId}`),
        supabase
          .from("stay_activities")
          .select(
            "id, stay_activity_type_id, scheduled_time, description, stay_activity_types ( name, color, icon ), stay_activity_recurrence ( frequency, ends_on )",
          )
          .eq("target_type", "pet")
          .eq("target_pet_id", petId!)
          .eq("clinic_id", clinicId!)
          .eq("is_active", true),
      ]);

      if (notesResult.error) throw notesResult.error;
      if (packsResult.error) throw packsResult.error;
      if (alertsResult.error) throw alertsResult.error;
      if (itemsResult.error) throw itemsResult.error;
      if (relationshipsResult.error) throw relationshipsResult.error;
      if (activitiesResult.error) throw activitiesResult.error;

      const activityRows = (activitiesResult.data ?? []) as unknown as Array<{
        id: string;
        stay_activity_type_id: string;
        scheduled_time: string | null;
        description: string | null;
        stay_activity_types: { name: string; color: string | null; icon: string | null } | null;
        stay_activity_recurrence: Array<{ frequency: string; ends_on: string | null }> | null;
      }>;

      return {
        ...EMPTY_PET_DAYCARE_HOTEL_DRAFT,
        notes: notesResult.data?.daycare_hotel_notes ?? "",
        packIds: ((packsResult.data ?? []) as unknown as Array<{ id: string }>).map((pack) => pack.id),
        alerts: (alertsResult.data ?? []).map((alert) => ({
          id: alert.id,
          label: alert.label,
          value: alert.value,
          color: alert.color,
        })),
        storedItems: (itemsResult.data ?? []).map((item) => ({
          id: item.id,
          itemName: item.item_name,
          storageLocation: item.storage_location,
        })),
        relationships: (relationshipsResult.data ?? []).map((row) => ({
          id: row.id,
          relatedPetId: row.pet_id === petId ? row.related_pet_id : row.pet_id,
          relationship: row.relationship as PetRelationshipKind,
        })),
        recurringActivities: activityRows.flatMap((row) => {
          const recurrence = row.stay_activity_recurrence?.[0];
          if (!recurrence || recurrence.frequency !== "daily" || recurrence.ends_on !== null) return [];
          return [{
            id: row.id,
            typeId: row.stay_activity_type_id,
            typeName: row.stay_activity_types?.name ?? "",
            typeColor: row.stay_activity_types?.color ?? null,
            typeIcon: row.stay_activity_types?.icon ?? null,
            description: row.description ?? "",
            scheduledTime: row.scheduled_time,
            recurring: true,
          }];
        }),
      };
    },
  });
}

export type DaycareHotelPetOption = {
  value: string;
  label: string;
  petId: string;
  petName: string;
  ownerName: string;
  petPhotoPath: string | null;
  petRecordNumber: string | null;
};

type PetSearchRpcRow = {
  pet_id: string | null;
  pet_name: string | null;
  pet_record_number: string | null;
  pet_death_date: string | null;
  pet_photo: { file_path: string } | null;
  owner_name: string | null;
};

function toOption(row: {
  petId: string;
  petName: string | null;
  ownerName: string | null;
  photoPath: string | null;
  recordNumber: string | null;
}): DaycareHotelPetOption {
  const petName = row.petName?.trim() || "";
  const ownerName = row.ownerName?.trim() || "";
  return {
    value: row.petId,
    label: ownerName ? `${petName} – ${ownerName}` : petName,
    petId: row.petId,
    petName,
    ownerName,
    petPhotoPath: row.photoPath,
    petRecordNumber: row.recordNumber,
  };
}

/**
 * Busca de pets para o vínculo de relações. Passa por `get_owners_with_pets`
 * porque essa RPC é o caminho de busca protegido por `patients:view`
 * (ver CLAUDE.md); consultar `pets` direto dependeria só da RLS.
 */
export function useDaycareHotelPetSearch(searchTerm: string, excludePetId?: string | null) {
  const { data: customerId } = useCurrentClinicCustomerId();
  const { hasPermission: canViewPatients, isLoading: isLoadingPermission } = useHasPermissionSync(
    "patients",
    "view",
  );
  const trimmed = searchTerm.trim();

  return useQuery<DaycareHotelPetOption[]>({
    queryKey: ["daycare-hotel-pet-search", customerId, trimmed, excludePetId ?? null],
    enabled: canViewPatients && !isLoadingPermission && !!customerId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!customerId) return [];
      const hasSearch = trimmed.length > 0;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_owners_with_pets", {
        p_customer_id: customerId,
        p_search: hasSearch ? trimmed : null,
        p_offset: 0,
        p_limit: hasSearch ? 50 : 25,
      });
      if (error) throw error;

      const rows = ((data as PetSearchRpcRow[] | null) ?? []).filter(
        (row) => row.pet_id && row.pet_name && !row.pet_death_date && row.pet_id !== excludePetId,
      );
      const seen = new Set<string>();
      const options: DaycareHotelPetOption[] = [];
      for (const row of rows) {
        if (seen.has(row.pet_id!)) continue;
        seen.add(row.pet_id!);
        options.push(
          toOption({
            petId: row.pet_id!,
            petName: row.pet_name,
            ownerName: row.owner_name,
            photoPath: row.pet_photo?.file_path ?? null,
            recordNumber: row.pet_record_number,
          }),
        );
      }
      return options;
    },
  });
}

/** Resolve os pets já vinculados (que podem estar fora da página de busca atual). */
export function useDaycareHotelPetsByIds(petIds: string[]) {
  const ids = Array.from(new Set(petIds.filter(Boolean))).sort();

  return useQuery<Record<string, DaycareHotelPetOption>>({
    queryKey: ["daycare-hotel-pets-by-id", ids],
    enabled: ids.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pets")
        .select("id, name, record_number, owner:owners(name), photo:pet_photos(file_path)")
        .in("id", ids);
      if (error) throw error;

      const byId: Record<string, DaycareHotelPetOption> = {};
      for (const row of data ?? []) {
        const owner = row.owner as { name: string | null } | null;
        const photo = row.photo as { file_path: string } | Array<{ file_path: string }> | null;
        const photoPath = Array.isArray(photo) ? photo[0]?.file_path ?? null : photo?.file_path ?? null;
        byId[row.id] = toOption({
          petId: row.id,
          petName: row.name,
          ownerName: owner?.name ?? null,
          photoPath,
          recordNumber: row.record_number,
        });
      }
      return byId;
    },
  });
}
